import { Title } from '@mantine/core';
import cx from 'clsx';
import { Fragment, type JSX } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import classes from './SideMenu.module.css';

export interface SubMenuProps {
  readonly name: string;
  readonly href: string;
}

export interface SideMenuProps {
  readonly title: string;
  readonly menu: { name: string | JSX.Element; href: string; subMenu?: SubMenuProps[] }[];
}

export function SideMenu(props: SideMenuProps): JSX.Element {
  const location = useLocation();
  
  // This function checks if a menu item should be considered active
  const isExactMatch = (href: string) => {
    if (href === '#') return false;
    
    // For index pages, handle the case where /documents redirects to /documents/forms/intake
    if (href === '/documents/forms/intake' && location.pathname === '/documents') {
      return true;
    }
    
    // Important: Adding URL params check to handle query params
    const hrefPath = href.split('?')[0]; // Remove query params for comparing paths
    const currentPath = location.pathname;
    
    // Extract potential ID from search params
    const searchParams = new URLSearchParams(location.search);
    const currentId = searchParams.get('id');
    
    // Check if href contains an ID that matches current ID
    const hrefSearchParams = new URLSearchParams(href.includes('?') ? href.split('?')[1] : '');
    const hrefId = hrefSearchParams.get('id');
    
    // Match by exact path or by ID if provided
    return currentPath === hrefPath && (!currentId || !hrefId || currentId === hrefId);
  };
  
  return (
    <div className={classes.container}>
      <Title order={4} className={classes.title}>
        {props.title}
      </Title>
      {props.menu.map((item, index) => (
        <Fragment key={`menu-item-${item.href}-${index}`}>
          {item.href === '#' ? (
            // Headers or dividers (not clickable)
            (<div key={`header-${index}`}>{item.name}</div>)
          ) : (
            // Use direct calculation of active state instead of relying on NavLink's isActive
            (<NavLink 
              to={item.href} 
              className={cx(
                classes.link, 
                isExactMatch(item.href) ? classes.linkActive : ''
              )}
              end={true}
            >
              <span>{item.name}</span>
            </NavLink>)
          )}
          {item.subMenu?.map((subItem, subIndex) => (
            <div key={`submenu-${subItem.href}-${subIndex}`} style={{ paddingLeft: 20 }}>
              <NavLink 
                to={subItem.href} 
                className={cx(
                  classes.link,
                  isExactMatch(subItem.href) ? classes.linkActive : ''
                )}
                end={true}
              >
                <span>{subItem.name}</span>
              </NavLink>
            </div>
          ))}
        </Fragment>
      ))}
    </div>
  );
}